import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Tag } from '@/components/Tag';
import { money, num, shortDate, date } from '@/lib/format';
import { documentLink } from './api';
import { EPS, documentInvoice, documentStatus } from './status';
import type { BusinessDocument, Scope } from './types';
import { Table, type Col } from './ui';
import s from './DocList.module.css';

function DocNo({ d }: { d: BusinessDocument }) {
  const l = documentLink(d.targetType, d.id);
  if (l.to) return <Link to={l.to} className={s.no} onClick={(e) => e.stopPropagation()}>{d.documentNo}</Link>;
  if (l.href) return <a href={l.href} className={s.no} title="在老版查看" onClick={(e) => e.stopPropagation()}>{d.documentNo}</a>;
  return <span className={s.noPlain}>{d.documentNo}</span>;
}

function Open({ v, children }: { v?: number; children?: ReactNode }) {
  if (v === undefined) return <span className="em">—</span>;
  return <span className={`tnum ${num(v) > EPS ? s.open : 'em'}`}>{children}{money(v)}</span>;
}

function Card({ d, scope }: { d: BusinessDocument; scope: Scope }) {
  const st = documentStatus(d.targetType, d.status || d.note);
  const inv = documentInvoice(d, scope);
  return (
    <li className={s.card}>
      <div className={s.h}>
        <b className="tnum"><DocNo d={d} /></b>
        {d.amount !== undefined && <strong className="tnum">{money(d.amount)}</strong>}
      </div>
      <div className={s.m}>
        <span><span className="tnum">{shortDate(d.documentDate)}</span> · {d.type}{st ? ` · ${st.label}` : ''}</span>
        {d.cashBalance !== undefined && <Open v={d.cashBalance}>未核销 </Open>}
      </div>
      <div className={s.f}>
        <Tag tone={inv.tone} sm>{inv.label}</Tag>
        {d.cashAllocated !== undefined && <span className="tnum">已核销 {money(d.cashAllocated)}</span>}
        {d.invoiceRequired && num(d.invoiceBalance) > EPS && <span className={`tnum ${s.warn}`}>待核票 {money(d.invoiceBalance)}</span>}
        {d.dueDate && <span className={s.due}>到期 / 交货 <span className="tnum">{shortDate(d.dueDate)}</span></span>}
      </div>
    </li>
  );
}

export function docTotals(docs: BusinessDocument[]) {
  return docs.reduce(
    (a, d) => ({
      amount: a.amount + num(d.amount),
      cashAllocated: a.cashAllocated + num(d.cashAllocated),
      cashBalance: a.cashBalance + num(d.cashBalance),
      invoiceAllocated: a.invoiceAllocated + num(d.invoiceAllocated),
      invoiceBalance: a.invoiceBalance + (d.invoiceRequired ? num(d.invoiceBalance) : 0),
      open: a.open + (num(d.cashBalance) > EPS ? 1 : 0),
    }),
    { amount: 0, cashAllocated: 0, cashBalance: 0, invoiceAllocated: 0, invoiceBalance: 0, open: 0 },
  );
}

/**
 * 业务单据（订单 / 采购单 / 淘宝单）。服务端已按单据日期升序、同日按单号排序——自动核销就是按这个顺序摊的。
 * 窄容器是卡片，宽容器是表格（容器查询切换）。
 */
export function DocList({ docs, scope, empty, mode = 'auto', footerAction, showTotals = true, compactDesktop = false }: { docs: BusinessDocument[]; scope: Scope; empty?: string; mode?: 'auto' | 'cards' | 'table' | 'compact' | 'compact-narrow'; footerAction?: ReactNode; showTotals?: boolean; compactDesktop?: boolean }) {
  if (!docs.length) return <p className={s.none}>{empty || '这个月没有业务单据。'}</p>;
  const amounts = docs.some((d) => d.amount !== undefined);
  const t = docTotals(docs);
  const compact = mode === 'compact' || mode === 'compact-narrow';
  const narrow = mode === 'compact-narrow';
  const cols: (Col<BusinessDocument> | false)[] = compact ? [
    { key: 'no', title: '单据', w: narrow ? 114 : 117, fix: true, render: (d) => <DocNo d={d} /> },
    { key: 'dd', title: '日期', w: narrow ? 60 : 90, render: (d) => shortDate(d.documentDate) },
    { key: 'type', title: '类型', w: narrow ? 68 : 83, render: (d) => scope === 'customer' && d.type === '业务订单' ? '销售订单' : d.type },
    amounts && { key: 'a', title: '金额', w: narrow ? 90 : 97, align: 'r', render: (d) => (d.amount === undefined ? '—' : money(d.amount)) },
    amounts && { key: 'ca', title: '已核销', w: narrow ? 90 : 97, align: 'r', render: (d) => (d.cashAllocated === undefined ? '—' : money(d.cashAllocated)) },
    amounts && { key: 'cb', title: '未核销', w: narrow ? 90 : 97, align: 'r', render: (d) => <Open v={d.cashBalance} /> },
    { key: 'inv', title: '开票', w: narrow ? 90 : 111, render: (d) => { const i = documentInvoice(d, scope); return <Tag tone={i.tone} sm>{i.label}</Tag>; } },
  ] : [
    { key: 'no', title: '单据号', w: 150, fix: true, render: (d) => <DocNo d={d} /> },
    { key: 'type', title: '类型', w: 92, render: (d) => d.type },
    { key: 'dd', title: '单据日期', w: 104, render: (d) => date(d.documentDate) },
    { key: 'due', title: '到期 / 交货', w: 104, render: (d) => date(d.dueDate) },
    { key: 'st', title: '状态', w: 88, render: (d) => documentStatus(d.targetType, d.status || d.note)?.label || <span className="em">—</span> },
    amounts && { key: 'a', title: '金额', w: 124, align: 'r', render: (d) => (d.amount === undefined ? '—' : money(d.amount)) },
    amounts && { key: 'ca', title: '已核销', w: 120, align: 'r', render: (d) => (d.cashAllocated === undefined ? '—' : money(d.cashAllocated)) },
    amounts && { key: 'cb', title: '未核销', w: 120, align: 'r', render: (d) => <Open v={d.cashBalance} /> },
    amounts && { key: 'ia', title: '已核票', w: 120, align: 'r', render: (d) => (d.invoiceAllocated === undefined ? '—' : money(d.invoiceAllocated)) },
    amounts && { key: 'ib', title: '待核票', w: 120, align: 'r', render: (d) => (d.invoiceRequired ? <Open v={d.invoiceBalance} /> : <span className="em">—</span>) },
    { key: 'inv', title: '发票口径', w: 96, render: (d) => { const i = documentInvoice(d, scope); return <Tag tone={i.tone} sm>{i.label}</Tag>; } },
  ];
  const list = cols.filter(Boolean) as Col<BusinessDocument>[];
  const foot = list.map((c) => {
    if (c.key === 'no') return `合计 ${docs.length} 张`;
    if (c.key === 'a') return money(t.amount);
    if (c.key === 'ca') return money(t.cashAllocated);
    if (c.key === 'cb') return <span className={t.cashBalance > EPS ? s.open : undefined}>{money(t.cashBalance)}</span>;
    if (c.key === 'ia') return money(t.invoiceAllocated);
    if (c.key === 'ib') return money(t.invoiceBalance);
    return '';
  });
  return (
      <div className={`${s.auto} ${compact ? s.compactMode : mode === 'table' ? s.tableMode : mode === 'cards' ? s.cardsMode : ''} ${compactDesktop ? s.compactDesktop : ''}`}>
      <div className={s.narrow}>
        <ul className={s.cards}>{docs.map((d) => <Card key={d.lineId || d.id} d={d} scope={scope} />)}</ul>
        {amounts && showTotals && (
          <div className={s.total}>
            <span>合计 {docs.length} 张{t.open ? ` · 未核销 ${t.open} 张` : ''}</span>
            <b className="tnum">{money(t.amount)}</b>
            {footerAction}
          </div>
        )}
      </div>
      <div className={s.wide}>
        <Table cols={list} rows={docs} rowKey={(d) => d.lineId || d.id} foot={amounts && showTotals ? foot : undefined} />
      </div>
    </div>
  );
}
